/**
 * SectorPolygonMap — a zero-dependency SVG rendering of KC sectors.
 *
 * Takes an array of items with `polygon: [[lat, lng], ...]` and renders
 * each polygon tinted by `health` (oversupplied/balanced/tight/etc).
 * Bounds are computed dynamically so the same component works for any
 * city as long as the polygons describe a single contiguous region.
 *
 * For the Sector Manager we also support `editable` mode — clicking on
 * the canvas appends a vertex, dragging a vertex updates it. The parent
 * owns the polygon state and receives `onPolygonChange(coords)`.
 */
import React, { useMemo, useRef, useState } from "react";

const HEALTH_FILL = {
  oversupplied: "rgba(16, 185, 129, 0.32)",
  balanced:     "rgba(234, 179, 8, 0.30)",
  tight:        "rgba(249, 115, 22, 0.32)",
  undersupplied:"rgba(239, 68, 68, 0.32)",
  inactive:     "rgba(100, 116, 139, 0.18)",
};
const HEALTH_STROKE = {
  oversupplied: "#10B981",
  balanced:     "#EAB308",
  tight:        "#F97316",
  undersupplied:"#EF4444",
  inactive:     "#94A3B8",
};

// Default KC bounding box if items don't supply enough info to compute one.
const KC_BBOX = { minLat: 38.7, maxLat: 39.35, minLng: -94.92, maxLng: -94.28 };

function flatPoints(items) {
  const pts = [];
  for (const it of items) {
    for (const p of it.polygon || []) {
      if (Array.isArray(p) && p.length >= 2) pts.push(p);
    }
  }
  return pts;
}

export default function SectorPolygonMap({
  items = [],
  selectedId,
  onSelect,
  height = 440,
  editable = false,
  drawingPolygon,           // [[lat,lng], ...] in editable mode
  onPolygonChange,          // (next) => void
  bboxItems,                // when editing a new sector, base bbox on all sectors
}) {
  const wrapRef = useRef(null);
  const [hoverId, setHoverId] = useState(null);

  // Bounds: use all known polygons if present, else KC default.
  const bbox = useMemo(() => {
    const all = flatPoints(bboxItems?.length ? bboxItems : items);
    if (drawingPolygon?.length) all.push(...drawingPolygon);
    if (!all.length) return KC_BBOX;
    const lats = all.map((p) => p[0]);
    const lngs = all.map((p) => p[1]);
    const padLat = 0.015;
    const padLng = 0.02;
    return {
      minLat: Math.min(...lats) - padLat,
      maxLat: Math.max(...lats) + padLat,
      minLng: Math.min(...lngs) - padLng,
      maxLng: Math.max(...lngs) + padLng,
    };
  }, [items, drawingPolygon, bboxItems]);

  const W = 1000;
  const H = height;
  const aspect = (bbox.maxLng - bbox.minLng) / (bbox.maxLat - bbox.minLat);
  const viewW = W;
  const viewH = Math.round(W / Math.max(aspect, 0.1));
  const renderH = Math.min(viewH, H * 1.7);

  const project = ([lat, lng]) => {
    const x = ((lng - bbox.minLng) / (bbox.maxLng - bbox.minLng)) * viewW;
    const y = (1 - (lat - bbox.minLat) / (bbox.maxLat - bbox.minLat)) * viewH;
    return [x, y];
  };

  const unproject = (clientX, clientY) => {
    if (!wrapRef.current) return null;
    const rect = wrapRef.current.getBoundingClientRect();
    const xRatio = (clientX - rect.left) / rect.width;
    const yRatio = (clientY - rect.top) / rect.height;
    const lng = bbox.minLng + xRatio * (bbox.maxLng - bbox.minLng);
    const lat = bbox.maxLat - yRatio * (bbox.maxLat - bbox.minLat);
    return [Number(lat.toFixed(5)), Number(lng.toFixed(5))];
  };

  const handleCanvasClick = (e) => {
    if (!editable) return;
    // Ignore clicks that bubble from vertex handles
    if (e.target.dataset?.vertex) return;
    const ll = unproject(e.clientX, e.clientY);
    if (!ll) return;
    onPolygonChange?.([...(drawingPolygon || []), ll]);
  };

  const handleVertexDrag = (idx, e) => {
    e.stopPropagation();
    const move = (ev) => {
      const ll = unproject(ev.clientX, ev.clientY);
      if (!ll) return;
      const next = [...(drawingPolygon || [])];
      next[idx] = ll;
      onPolygonChange?.(next);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return (
    <div
      ref={wrapRef}
      className={`relative w-full rounded-xl overflow-hidden ${editable ? "bg-[#F4F4F6] cursor-crosshair" : "bg-[#F4F4F6]"}`}
      style={{ height }}
      data-testid="sector-polygon-map"
    >
      <svg
        viewBox={`0 0 ${viewW} ${renderH}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height={height}
        onClick={handleCanvasClick}
      >
        {/* Subtle grid */}
        <defs>
          <pattern id="admGrid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#admGrid)" />

        {/* Existing polygons */}
        {items.map((it) => {
          const pts = (it.polygon || []).map(project).map((p) => p.join(",")).join(" ");
          const isSel = selectedId === it.id || hoverId === it.id;
          return (
            <g key={it.id}>
              <polygon
                points={pts}
                fill={HEALTH_FILL[it.health] || HEALTH_FILL.inactive}
                stroke={HEALTH_STROKE[it.health] || HEALTH_STROKE.inactive}
                strokeWidth={isSel ? 3 : 1.4}
                style={{ cursor: onSelect ? "pointer" : "default", transition: "stroke-width .2s" }}
                onMouseEnter={() => setHoverId(it.id)}
                onMouseLeave={() => setHoverId(null)}
                onClick={(e) => { e.stopPropagation(); onSelect?.(it.id); }}
              />
              {it.center && (() => {
                const [cx, cy] = project(it.center);
                const label = it.name || "—";
                const padded = Math.max(label.length * 6 + 26, 70);
                return (
                  <g pointerEvents="none">
                    <rect x={cx - padded / 2} y={cy - 14} width={padded} height="26" rx="13" fill="#081B4B" />
                    <text x={cx} y={cy + 4} textAnchor="middle" fontSize="13" fontWeight="600" fill="#fff">
                      {label} · {(it.open_blocks ?? 0)}
                    </text>
                  </g>
                );
              })()}
            </g>
          );
        })}

        {/* Drawing layer (editable mode) */}
        {editable && drawingPolygon?.length >= 1 && (() => {
          const pts = drawingPolygon.map(project);
          const ptsStr = pts.map((p) => p.join(",")).join(" ");
          return (
            <g>
              {drawingPolygon.length >= 3 && (
                <polygon
                  points={ptsStr}
                  fill="rgba(249,115,22,0.18)"
                  stroke="#F97316"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                />
              )}
              {drawingPolygon.length === 2 && (
                <polyline
                  points={ptsStr}
                  fill="none"
                  stroke="#F97316"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                />
              )}
              {pts.map((p, i) => (
                <g key={i} onMouseDown={(e) => handleVertexDrag(i, e)} style={{ cursor: "grab" }}>
                  <circle cx={p[0]} cy={p[1]} r="6" fill="#fff" stroke="#F97316" strokeWidth="2" data-vertex="1" />
                </g>
              ))}
            </g>
          );
        })()}
      </svg>

      {editable && (
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3 bg-[#081B4B] text-white rounded-xl px-3 py-2 text-[11px]">
          <span>Click the map to add a vertex. Drag a vertex to move. Minimum 3 vertices.</span>
          <span className="tracking-wider uppercase">
            {(drawingPolygon?.length || 0)} pts
          </span>
        </div>
      )}
    </div>
  );
}
